import { FC, HTMLAttributes } from "react";
import clsx from "classnames";
import { useStyles } from "./Dialog.style";
import Button, { IButtonProps } from "../Button/Button";
import {
  Typography,
  TypographyColor,
  TypographyVariant,
} from "../Typography/Typography";
import { useDialogControl } from "../../hooks/useDialogControl";

interface IButtonAction {
  text: string | React.ReactNode;
  icon?: React.ReactNode;
  loading?: boolean;
}

interface IDialogProps {
  open: boolean;
  mainAction?: IButtonAction & IButtonProps & HTMLAttributes<HTMLDivElement>;
  secondAction?: IButtonAction & IButtonProps & HTMLAttributes<HTMLDivElement>;
  title?: string;
  description?: string;
  icon?: React.ReactNode;
  onClose?: () => void;
  stickBottom?: boolean;
  enableScrollBackground?: boolean;
  reverseButtons?: boolean;
}

export const Dialog: FC<IDialogProps & HTMLAttributes<HTMLDialogElement>> = ({
  open = false,
  icon,
  mainAction,
  secondAction,
  children,
  title,
  description,
  reverseButtons,
  ...otherProps
}) => {
  const classes = useStyles();
  const ref = useDialogControl(open);

  if (!open) return null;
  return (
    <dialog
      {...otherProps}
      ref={ref}
      className={clsx(classes.root, { open }, otherProps.className)}
    >
      {icon && <div className={classes.icon}>{icon}</div>}
      <div className={classes.content}>
        {title && (
          <Typography
            variant={TypographyVariant.BodyB3Bold}
            color={TypographyColor.Text1}
            data-testid="title-dialog"
          >
            {title}
          </Typography>
        )}
        {description && (
          <Typography
            variant={TypographyVariant.BodyB6Regular}
            color={TypographyColor.Text2}
            data-testid="description-dialog"
          >
            {description}
          </Typography>
        )}
        {children}
      </div>
      <div className={classes.btns}>
        {secondAction && (
          <Button
            variant="outlined"
            fullWidth
            loading={secondAction?.loading}
            {...secondAction}
            data-testid="accept-action"
          >
            {secondAction.icon} {secondAction.text}
          </Button>
        )}
        {mainAction && (
          <Button
            variant="contained"
            fullWidth
            loading={mainAction?.loading}
            {...mainAction}
            data-testid="cancel-action"
          >
            {mainAction?.icon} {mainAction?.text || "Ok"}
          </Button>
        )}
      </div>
    </dialog>
  );
};
